import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';

type DownloadType = 'setup' | 'zip' | 'uninstall' | 'powershell';

interface TokenDuration {
  hours: number;
  label: string;
}

const TOKEN_DURATIONS: TokenDuration[] = [
  { hours: 24, label: '1 Day' },
  { hours: 168, label: '1 Week' },
  { hours: 720, label: '1 Month' },
  { hours: 1440, label: '2 Months' },
  { hours: 2160, label: '3 Months' },
];

const AgentDownload: React.FC = () => {
  const { user, getAccessToken } = useAuth();
  const [downloading, setDownloading] = useState<DownloadType | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [generatingScript, setGeneratingScript] = useState(false);
  const [scriptContent, setScriptContent] = useState<string | null>(null);
  const [selectedDuration, setSelectedDuration] = useState<number>(24);
  const [tokenExpiresAt, setTokenExpiresAt] = useState<string | null>(null);

  const getApiBase = () =>
    import.meta.env.VITE_API_URL_PRODUCTION || 'https://app-anchor-api-production.azurewebsites.net';

  const handleDownload = async (type: DownloadType) => {
    if (!user?.tenantId) {
      setError('Tenant ID not available. Please log in again.');
      return;
    }

    setDownloading(type);
    setError(null);

    try {
      const token = await getAccessToken();
      if (!token) throw new Error('Unable to get authentication token');

      const apiBase = getApiBase();

      if (type === 'powershell') {
        let text = scriptContent;
        if (!text) {
          const response = await fetch(
            `${apiBase}/api/v1/tenants/${user.tenantId}/agent/powershell-installer?tokenExpiry=${selectedDuration}`,
            { headers: { 'Authorization': `Bearer ${token}` } }
          );
          if (!response.ok) {
            const errText = await response.text();
            let errMsg = 'Download failed';
            try { errMsg = JSON.parse(errText).error || errMsg; } catch {}
            throw new Error(errMsg);
          }
          text = await response.text();
        }
        const blob = new Blob([text], { type: 'text/plain' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'Install-DataAnchor.ps1';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        setDownloading(null);
        return;
      }

      const endpoint = type === 'setup'
        ? `${apiBase}/api/v1/tenants/${user.tenantId}/agent/setup`
        : type === 'uninstall'
        ? `${apiBase}/api/v1/tenants/${user.tenantId}/agent/uninstall`
        : `${apiBase}/api/v1/tenants/${user.tenantId}/agent/download`;

      const response = await fetch(endpoint, {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({ error: 'Download failed' }));
        throw new Error(data.error || `HTTP ${response.status}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const defaultName = type === 'setup' ? 'dataanchorsetup31.exe' : type === 'uninstall' ? 'DataAnchorUninstall.exe' : 'anchor-agent.zip';
      a.download = response.headers.get('content-disposition')?.split('filename=')[1]?.replace(/"/g, '') || defaultName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();

      if (type === 'setup') {
        const configResponse = await fetch(
          `${apiBase}/api/v1/tenants/${user.tenantId}/agent/config`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        if (configResponse.ok) {
          const configData = await configResponse.json();
          const configBlob = new Blob([JSON.stringify(configData, null, 2)], { type: 'application/json' });
          const configUrl = window.URL.createObjectURL(configBlob);
          const configLink = document.createElement('a');
          configLink.href = configUrl;
          configLink.download = 'client.json';
          document.body.appendChild(configLink);
          await new Promise(r => setTimeout(r, 500));
          configLink.click();
          window.URL.revokeObjectURL(configUrl);
          configLink.remove();
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to download');
    } finally {
      setDownloading(null);
    }
  };

  const handleGenerateScript = async () => {
    if (!user?.tenantId) {
      setError('Tenant ID not available. Please log in again.');
      return;
    }

    setGeneratingScript(true);
    setError(null);
    setScriptContent(null);
    setTokenExpiresAt(null);

    try {
      const token = await getAccessToken();
      if (!token) throw new Error('Unable to get authentication token');

      const apiBase = getApiBase();
      const response = await fetch(
        `${apiBase}/api/v1/tenants/${user.tenantId}/agent/powershell-installer?tokenExpiry=${selectedDuration}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (!response.ok) {
        const errText = await response.text();
        let errMsg = 'Generation failed';
        try { errMsg = JSON.parse(errText).error || errMsg; } catch {}
        throw new Error(errMsg);
      }

      const text = await response.text();
      setScriptContent(text);

      const expiryMatch = text.match(/# Registration token valid until: (.+)/);
      if (expiryMatch?.[1] && expiryMatch[1] !== 'unknown') {
        setTokenExpiresAt(expiryMatch[1]);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate script');
    } finally {
      setGeneratingScript(false);
    }
  };

  const handleCopyScript = async () => {
    if (!scriptContent) return;
    try {
      await navigator.clipboard.writeText(scriptContent);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError('Failed to copy to clipboard');
    }
  };

  const DownloadIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
      <polyline points="7 10 12 15 17 10"/>
      <line x1="12" y1="15" x2="12" y2="3"/>
    </svg>
  );

  const TerminalIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="4 17 10 11 4 5"/>
      <line x1="12" y1="19" x2="20" y2="19"/>
    </svg>
  );

  const CopyIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
    </svg>
  );

  const CheckIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12"/>
    </svg>
  );

  const Spinner = () => (
    <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
    </svg>
  );

  return (
    <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-6">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-lg font-medium text-white mb-1">Download Agent</h3>
          <p className="text-sm text-white/40">
            Download a pre-configured agent installer for your organization.
            The agent will automatically connect to your Data Anchor tenant.
          </p>
        </div>
      </div>

      {/* Platform selector */}
      <div className="flex gap-3 mb-5">
        <button className="flex items-center gap-2 py-2.5 px-4 bg-anchor-blue/10 border border-anchor-blue/30 rounded-lg text-sm text-anchor-blue cursor-default">
          <svg width="16" height="16" viewBox="0 0 21 21" fill="none">
            <rect x="1" y="1" width="9" height="9" fill="#F25022"/>
            <rect x="11" y="1" width="9" height="9" fill="#7FBA00"/>
            <rect x="1" y="11" width="9" height="9" fill="#00A4EF"/>
            <rect x="11" y="11" width="9" height="9" fill="#FFB900"/>
          </svg>
          Windows
        </button>
        <button className="flex items-center gap-2 py-2.5 px-4 bg-white/[0.02] border border-white/[0.06] rounded-lg text-sm text-white/20 cursor-not-allowed" disabled>
          Linux (coming soon)
        </button>
        <button className="flex items-center gap-2 py-2.5 px-4 bg-white/[0.02] border border-white/[0.06] rounded-lg text-sm text-white/20 cursor-not-allowed" disabled>
          macOS (coming soon)
        </button>
      </div>

      {/* Download buttons */}
      <div className="flex gap-3">
        {/* Primary: Setup EXE */}
        <button
          onClick={() => handleDownload('setup')}
          disabled={downloading !== null}
          className="flex-1 py-3 px-5 bg-anchor-blue border-none rounded-lg text-white text-sm font-medium cursor-pointer flex items-center justify-center gap-2.5 transition-all duration-300 hover:bg-[#3636DD] hover:-translate-y-px hover:shadow-[0_8px_28px_rgba(69,69,255,0.35)] disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {downloading === 'setup' ? (
            <><Spinner /> Generating installer...</>
          ) : (
            <><DownloadIcon /> Setup Installer (.exe)</>
          )}
        </button>

        {/* Secondary: ZIP */}
        <button
          onClick={() => handleDownload('zip')}
          disabled={downloading !== null}
          className="py-3 px-5 bg-white/[0.04] border border-white/[0.08] rounded-lg text-white/50 text-sm cursor-pointer flex items-center justify-center gap-2.5 transition-all duration-300 hover:bg-white/[0.06] hover:text-white/70 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {downloading === 'zip' ? (
            <><Spinner /> Generating...</>
          ) : (
            <><DownloadIcon /> ZIP Package</>
          )}
        </button>

        {/* Uninstaller */}
        <button
          onClick={() => handleDownload('uninstall')}
          disabled={downloading !== null}
          className="py-3 px-5 bg-white/[0.04] border border-red-500/20 rounded-lg text-red-400/60 text-sm cursor-pointer flex items-center justify-center gap-2.5 transition-all duration-300 hover:bg-red-500/10 hover:text-red-400 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {downloading === 'uninstall' ? (
            <><Spinner /> Downloading...</>
          ) : (
            <><DownloadIcon /> Uninstaller</>
          )}
        </button>
      </div>

      {/* PowerShell Installer Section */}
      <div className="mt-5 pt-4 border-t border-white/[0.04]">
        <div className="flex items-center gap-2 mb-3">
          <TerminalIcon />
          <h4 className="text-sm font-medium text-white">PowerShell Installer</h4>
          <span className="text-[10px] py-0.5 px-2 rounded-full bg-anchor-blue/10 text-anchor-blue border border-anchor-blue/20">
            One-click deploy
          </span>
        </div>
        <p className="text-xs text-white/40 mb-3">
          Generate a self-contained PowerShell script pre-configured for your tenant.
          Run it as Administrator on any Windows endpoint to download, install, and register the agent automatically.
        </p>

        <div className="mb-3">
          <label className="text-xs text-white/50 mb-2 block">Token validity</label>
          <div className="flex gap-2">
            {TOKEN_DURATIONS.map((d) => (
              <button
                key={d.hours}
                onClick={() => { setSelectedDuration(d.hours); setScriptContent(null); setTokenExpiresAt(null); }}
                className={`py-1.5 px-3 rounded-lg text-xs transition-all duration-200 border ${
                  selectedDuration === d.hours
                    ? 'bg-anchor-blue/20 border-anchor-blue/40 text-anchor-blue'
                    : 'bg-white/[0.02] border-white/[0.08] text-white/40 hover:bg-white/[0.04] hover:text-white/60'
                }`}
              >
                {d.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex gap-3 mb-3">
          <button
            onClick={handleGenerateScript}
            disabled={generatingScript || downloading !== null}
            className="py-2.5 px-4 bg-[#012456]/40 border border-[#012456]/60 rounded-lg text-[#5B9BD5] text-sm cursor-pointer flex items-center justify-center gap-2 transition-all duration-300 hover:bg-[#012456]/60 hover:text-[#7BB8E8] hover:border-[#5B9BD5]/40 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {generatingScript ? (
              <><Spinner /> Generating...</>
            ) : (
              <><TerminalIcon /> Generate Script</>
            )}
          </button>

          <button
            onClick={() => handleDownload('powershell')}
            disabled={downloading !== null}
            className="py-2.5 px-4 bg-white/[0.04] border border-white/[0.08] rounded-lg text-white/50 text-sm cursor-pointer flex items-center justify-center gap-2 transition-all duration-300 hover:bg-white/[0.06] hover:text-white/70 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {downloading === 'powershell' ? (
              <><Spinner /> Downloading...</>
            ) : (
              <><DownloadIcon /> Download .ps1</>
            )}
          </button>
        </div>

        {tokenExpiresAt && (
          <div className="mb-3 p-2.5 bg-yellow-500/5 border border-yellow-500/20 rounded-lg flex items-center gap-2">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-yellow-400 flex-shrink-0">
              <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
            </svg>
            <span className="text-xs text-yellow-300/80">
              Registration token valid until <strong className="text-yellow-300">{tokenExpiresAt}</strong>
            </span>
          </div>
        )}

        {/* Script preview and copy area */}
        {scriptContent && (
          <div className="relative bg-[#012456]/30 border border-[#012456]/40 rounded-lg overflow-hidden">
            <div className="flex items-center justify-between px-3 py-2 bg-[#012456]/20 border-b border-[#012456]/30">
              <span className="text-[10px] text-[#5B9BD5]/60 font-mono">Install-DataAnchor.ps1</span>
              <button
                onClick={handleCopyScript}
                className="flex items-center gap-1.5 text-[10px] text-[#5B9BD5] hover:text-white transition-colors px-2 py-1 rounded border border-[#5B9BD5]/30 hover:border-[#5B9BD5]/60 bg-[#012456]/40"
              >
                {copied ? <><CheckIcon /> Copied</> : <><CopyIcon /> Copy script</>}
              </button>
            </div>
            <pre className="p-3 text-xs text-[#5B9BD5]/80 font-mono overflow-x-auto max-h-48 overflow-y-auto leading-relaxed">
              {scriptContent.split('\n').slice(0, 20).join('\n')}
              {scriptContent.split('\n').length > 20 && '\n\n# ... (truncated preview — download or copy for full script)'}
            </pre>
          </div>
        )}
      </div>

      {error && (
        <div className="mt-3 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
          <p className="text-xs text-red-300">{error}</p>
        </div>
      )}

      {/* Instructions */}
      <div className="mt-5 pt-4 border-t border-white/[0.04]">
        <h4 className="text-xs font-medium text-white/50 tracking-wider uppercase mb-3">Quick Start</h4>
        <ol className="space-y-2 text-sm text-white/40">
          <li className="flex gap-2">
            <span className="text-anchor-blue font-medium">1.</span>
            Click <strong className="text-white/60">Setup Installer</strong> to download the installer
          </li>
          <li className="flex gap-2">
            <span className="text-anchor-blue font-medium">2.</span>
            Run <code className="text-white/60 bg-white/[0.04] px-1.5 py-0.5 rounded text-xs">dataanchorsetup31.exe</code> as Administrator
          </li>
          <li className="flex gap-2">
            <span className="text-anchor-blue font-medium">3.</span>
            The installer downloads dependencies and configures the agent automatically
          </li>
        </ol>
        <p className="mt-3 text-xs text-white/25">
          Alternatively, use the <strong className="text-white/40">PowerShell Installer</strong> above for a script-based deployment, or download the ZIP package for manual installation.
        </p>
      </div>
    </div>
  );
};

export default AgentDownload;
